import React, {useEffect} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Text,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepSeparator,
  StepTitle,
  StepDescription,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
  SimpleGrid,
  Select,
  Stack,
  Divider,
  Textarea,
  useToast,
  GridItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Avatar,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { getData } from '../../utils/httpClient';
import DocumentInput from '../common/DocumentInput';

const steps = [
  { title: 'Datos Personales', description: 'Datos personales del alumno' },
  { title: 'Contacto', description: 'Datos de contacto' },
  { title: 'Otros', description: 'Otros datos' },
  { title: 'Escolaridad', description: 'Datos escolares' },
  { title: 'Confirmar', description: 'Verifica los datos antes de agregar' },
];

export default function CreateModal({
  isOpen,
  onClose,
  formData,
  setFormData,
  onConfirm,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [sexTypes, setSexTypes] = useState([]);
  const [existingPerson, setExistingPerson] = useState(null);
  const [documentSearched, setDocumentSearched] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const toast = useToast();

  const getDocumentTypes = async () => {
    try {
      const response = await getData('/documentTypes');
      setDocumentTypes(response?.payload?.documentTypes || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los tipos de documento.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getSexTypes = async () => {
    try {
      const response = await getData('/sexTypes');
      setSexTypes(response?.payload?.sexTypes || []);
    } catch (error) {
      toast({
        title: 'Error al cargar los tipos de sexo.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getDocumentTypes();
    getSexTypes();
  }, []);

  const handleNextStep = () => {
    console.log('activeStep', activeStep);
    console.log('formData', formData);
    if (validateStep()) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    } else {
      // Mostrar mensaje de error si los campos no están completos
      toast({
        title: 'Por favor completa todos los campos requeridos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePrevStep = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Reiniciar el estado de búsqueda si se cambia el documento
    if (name === 'document' && documentSearched) {
      setDocumentSearched(false);
      setExistingPerson(null);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0]; // Obtener el primer archivo cargado
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result; // El archivo cargado como base64
        setFormData((prevData) => ({
          ...prevData,
          form_69A: base64, // Aquí puedes almacenar el archivo como base64 o enviarlo como `FormData`
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Archivo inválido',
          description: 'Por favor seleccione una imagen válida',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Archivo muy grande',
          description: 'La imagen no debe superar los 5MB',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData((prevData) => ({
          ...prevData,
          photo: base64,
        }));
        setPhotoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const searchByDocument = async () => {
    if (!formData.document.trim()) {
      toast({
        title: 'Documento requerido',
        description: 'Por favor ingrese un número de documento para buscar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSearching(true);
    try {
      const response = await getData(`/person/search/document/${formData.document.trim()}`);
      const { person, exists } = response.payload;
      
      setExistingPerson(person);
      setDocumentSearched(true);
      
      if (exists) {
        toast({
          title: 'Persona encontrada',
          description: `Ya existe una persona registrada con el documento ${formData.document}`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Documento disponible',
          description: 'No se encontró ninguna persona con este documento. Puede continuar con el registro.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error en la búsqueda',
        description: 'No se pudo realizar la búsqueda. Intente nuevamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setExistingPerson(null);
    setDocumentSearched(false);
    setFormData(prev => ({
      ...prev,
      document: ''
    }));
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        // Validación para el Step 1 (Datos Personales)
        // Verificar que se haya hecho la búsqueda por documento
        if (!documentSearched) {
          toast({
            title: 'Verificación de documento requerida',
            description: 'Debe buscar por documento para verificar que la persona no esté registrada',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          return false;
        }
        
        // Verificar que no exista una persona con ese documento
        if (existingPerson) {
          toast({
            title: 'Persona ya registrada',
            description: 'Ya existe una persona registrada con este documento',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return false;
        }

        return (
          formData.name &&
          formData.lastName &&
          formData.documentTypeId &&
          formData.document &&
          formData.birthDate &&
          formData.sexId
        );
      case 1:
        // Validación para el Step 2 (Contacto)
        return (
          formData.cellPhone &&
          formData.email &&
          formData.street &&
          formData.number &&
          formData.country &&
          formData.city
        );
      case 2:
        // Validación para el Step 3 (Otros Datos)
        return formData.emergencyContact && formData.emergencyContactPhone;
      case 3:
        // Validación para el Step 4 (Escolaridad)
        // En UI no se marca ningún campo con "*" obligatorio, así que
        // no debería bloquear el avance aunque estén incompletos.
        return true;
      default:
        return true;
    }
  };

  const validateForm = () => {
    const errors = [];
    
    // Verificar campos obligatorios de información personal
    if (!formData.name.trim()) errors.push("El nombre es obligatorio");
    if (!formData.lastName.trim()) errors.push("El apellido es obligatorio");
    if (!formData.documentTypeId) errors.push("El tipo de documento es obligatorio");
    if (!formData.document.trim()) errors.push("El número de documento es obligatorio");
    if (!formData.birthDate) errors.push("La fecha de nacimiento es obligatoria");
    if (!formData.sexId) errors.push("El sexo es obligatorio");
    
    // Verificar campos obligatorios de dirección
    if (!formData.street.trim()) errors.push("La calle es obligatoria");
    if (!formData.number.trim()) errors.push("El número de dirección es obligatorio");
    if (!formData.city.trim()) errors.push("La ciudad es obligatoria");
    if (!formData.country.trim()) errors.push("El país es obligatorio");
    
    // Verificar que el documento se haya buscado
    if (!documentSearched && formData.document.trim()) {
      errors.push("Debe verificar el documento antes de continuar");
    }
    
    // Verificar que no existe una persona con ese documento
    if (existingPerson) {
      errors.push("Ya existe una persona registrada con este documento");
    }
    
    return errors;
  };

  const handleSave = () => {
    const validationErrors = validateForm();
    
    if (validationErrors.length > 0) {
      alert("⚠️ Faltan datos obligatorios:\n\n" + validationErrors.join("\n"));
      return;
    }
    
    // Verificar si hay foto
    const hasPhoto = photoPreview ? "✅" : "❌";
    const photoMessage = photoPreview ? 
      "Foto agregada correctamente" : 
      "No se agregó foto (recomendado para identificación)";
    
    const confirmMessage = `
📋 Resumen del registro:
• Nombre: ${formData.name} ${formData.lastName}
• Documento: ${formData.document}
• ${photoMessage} ${hasPhoto}

¿Confirma que desea guardar este estudiante?`;
    
    if (window.confirm(confirmMessage)) {
      onConfirm();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        maxWidth={{ base: '100%', md: '80%' }}
        minHeight={'85vh'}
        maxHeight={'85vh'}
        overflowY="auto"
      >
        <ModalHeader mb={5}>Agregar Estudiante</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
          >
            <Box
              flex="0 0 30%"
              mr={6}
              display={{ base: 'none', xl: 'block' }}
              height="60vh"
            >
              <Stepper
                index={activeStep}
                orientation="vertical"
                gap="4"
                height="100%"
                display="flex"
                flexDirection="column"
              >
                {steps.map((step, index) => (
                  <Step key={index} flex="1">
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>
                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box flex="1" mb={5}>
              {activeStep === 0 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  {/* Sección de foto */}
                  <Box gridColumn="span 2" mb={6}>
                    <Text fontSize="lg" fontWeight="bold" mb={3} color="gray.700">
                      📷 Foto del Estudiante
                    </Text>
                    <HStack spacing={6} align="start">
                      <VStack spacing={3}>
                        <Avatar 
                          size="2xl" 
                          src={photoPreview} 
                          name={formData.name ? `${formData.name} ${formData.lastName}` : 'Estudiante'}
                          bg="gray.200"
                        />
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                          Vista previa
                        </Text>
                      </VStack>
                      <VStack align="start" flex={1}>
                        <FormControl>
                          <FormLabel>Seleccionar foto</FormLabel>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                            bg="white"
                            borderColor="gray.300"
                            _hover={{ borderColor: "blue.400" }}
                            _focus={{ borderColor: "blue.500" }}
                          />
                        </FormControl>
                        <Text fontSize="xs" color="gray.500">
                          Formatos soportados: JPG, PNG, GIF (máximo 5MB)
                        </Text>
                        {photoPreview && (
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => {
                              setPhotoPreview(null);
                              setFormData(prev => ({ ...prev, photo: null }));
                            }}
                          >
                            Quitar foto
                          </Button>
                        )}
                      </VStack>
                    </HStack>
                  </Box>

                  {/* Datos */}
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={2}
                    gridColumn="span 2"
                  >
                    Datos personales
                  </Text>

                  {/* Nombre */}
                  <FormControl id="name" isRequired>
                    <FormLabel>Nombre *</FormLabel>
                    <Input
                      placeholder="Nombre"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Segundo Nombre */}
                  <FormControl id="secondName">
                    <FormLabel>Segundo Nombre</FormLabel>
                    <Input
                      placeholder="Segundo Nombre"
                      name="secondName"
                      value={formData.secondName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Apellido */}
                  <FormControl id="lastName" isRequired>
                    <FormLabel>Apellido *</FormLabel>
                    <Input
                      placeholder="Apellido"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Segundo Apellido */}
                  <FormControl id="maternalLastName">
                    <FormLabel>Segundo Apellido</FormLabel>
                    <Input
                      placeholder="SegundoApellido"
                      name="maternalLastName"
                      value={formData.maternalLastName}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Tipo de Documento */}
                  <FormControl id="documentTypeId" isRequired>
                    <FormLabel>Tipo de Documento *</FormLabel>
                    <Select
                      name="documentTypeId"
                      value={formData.documentTypeId}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccione</option>
                      {documentTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Documento */}
                  <FormControl id="document" isRequired>
                    <FormLabel>Número de Documento *</FormLabel>
                    <InputGroup>
                      <DocumentInput
                        placeholder="Documento"
                        name="document"
                        value={formData.document}
                        onChange={handleInputChange}
                        bg={documentSearched && !existingPerson ? "green.50" : "white"}
                        borderColor={documentSearched && !existingPerson ? "green.400" : "gray.300"}
                        _focus={{
                          borderColor: documentSearched && !existingPerson ? "green.500" : "blue.500",
                          boxShadow: documentSearched && !existingPerson ? "0 0 0 1px green.500" : "0 0 0 1px blue.500"
                        }}
                        _hover={{
                          borderColor: documentSearched && !existingPerson ? "green.500" : "gray.400"
                        }}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label="Verificar documento"
                          icon={<SearchIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme={documentSearched && !existingPerson ? "green" : "blue"}
                          onClick={searchByDocument}
                          isDisabled={!formData.document || !formData.documentTypeId}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {documentSearched && !existingPerson && (
                      <Text fontSize="xs" color="green.600" mt={1} fontWeight="medium">
                        ✓ Documento verificado y disponible
                      </Text>
                    )}
                    {existingPerson && (
                      <Text fontSize="xs" color="red.600" mt={1} fontWeight="medium">
                        ⚠ Este documento ya está registrado
                      </Text>
                    )}
                  </FormControl>

                  {/* Fecha de Nacimiento */}
                  <FormControl id="birthDate" isRequired>
                    <FormLabel>Fecha de Nacimiento *</FormLabel>
                    <Input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Sexo */}
                  <FormControl id="sexId" isRequired>
                    <FormLabel>Sexo *</FormLabel>
                    <Select
                      name="sexId"
                      value={formData.sexId}
                      onChange={handleInputChange}
                    >
                      <option value="">Seleccione</option>
                      {sexTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.description}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </SimpleGrid>
              )}
              {activeStep === 1 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  {/* Contacto */}
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={4}
                    gridColumn="span 2"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    📞 Información de Contacto
                  </Text>

                  {/* Teléfono */}
                  <FormControl id="phone">
                    <FormLabel>Teléfono</FormLabel>
                    <Input
                      placeholder="Teléfono"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Celular */}
                  <FormControl id="cellPhone" isRequired>
                    <FormLabel>Celular</FormLabel>
                    <Input
                      placeholder="Celular"
                      name="cellPhone"
                      value={formData.cellPhone}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Correo Electrónico */}
                  <FormControl id="email" isRequired>
                    <FormLabel>Correo Electrónico</FormLabel>
                    <Input
                      placeholder="Correo Electrónico"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Divider */}
                  <Divider my={6} gridColumn="span 2" />

                  {/* Dirección */}
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={4}
                    gridColumn="span 2"
                  >
                    📍 Información de Dirección
                  </Text>
                  
                  {/* Calle */}
                  <FormControl id="street" isRequired>
                    <FormLabel>Calle *</FormLabel>
                    <Input
                      placeholder="Ej: Av. Corrientes, Calle San Martín"
                      name="street"
                      value={formData.street}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Número */}
                  <FormControl id="number" isRequired>
                    <FormLabel>Número *</FormLabel>
                    <Input
                      placeholder="Ej: 1234, 456 bis"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Departamento/Piso */}
                  <FormControl id="apartment">
                    <FormLabel>Depto/Piso</FormLabel>
                    <Input
                      placeholder="Ej: 3A, Piso 2"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Barrio */}
                  <FormControl id="neighborhood">
                    <FormLabel>Barrio</FormLabel>
                    <Input
                      placeholder="Barrio"
                      name="neighborhood"
                      value={formData.neighborhood}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Ciudad */}
                  <FormControl id="city" isRequired>
                    <FormLabel>Ciudad *</FormLabel>
                    <Input
                      placeholder="Ciudad"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Código Postal */}
                  <FormControl id="postalCode">
                    <FormLabel>Código Postal</FormLabel>
                    <Input
                      placeholder="Código Postal"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* País */}
                  <FormControl id="country" isRequired>
                    <FormLabel>País *</FormLabel>
                    <Input
                      placeholder="País"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </SimpleGrid>
              )}
              {activeStep === 2 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  {/* Contacto de Emergencia */}
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={4}
                    gridColumn="span 2"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    🚨 Contacto de Emergencia
                  </Text>

                  {/* Contacto de Emergencia */}
                  <FormControl id="emergencyContact" isRequired>
                    <FormLabel>Contacto de Emergencia *</FormLabel>
                    <Input
                      placeholder="Nombre del contacto de emergencia"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Teléfono de Emergencia */}
                  <FormControl id="emergencyContactPhone" isRequired>
                    <FormLabel>Teléfono de Emergencia</FormLabel>
                    <Input
                      placeholder="Teléfono de contacto de emergencia"
                      name="emergencyContactPhone"
                      value={formData.emergencyContactPhone}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Sociedad médica (campo DB: medicalInsurance) */}
                  <FormControl id="medicalInsurance">
                    <FormLabel>Sociedad médica</FormLabel>
                    <Input
                      placeholder="Sociedad médica"
                      name="medicalInsurance"
                      value={formData.medicalInsurance}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Emergencia móvil (campo DB: mobileEmergency) */}
                  <FormControl id="mobileEmergency">
                    <FormLabel>Emergencia móvil</FormLabel>
                    <Input
                      placeholder="Emergencia móvil"
                      name="mobileEmergency"
                      value={formData.mobileEmergency}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </SimpleGrid>
              )}
              {activeStep === 3 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  {/* Colegio */}
                  <Text
                    fontSize="lg"
                    fontWeight="bold"
                    mb={4}
                    gridColumn="span 2"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    🎓 Información Académica
                  </Text>

                  <FormControl id="school">
                    <FormLabel>Colegio</FormLabel>
                    <Input
                      placeholder="Nombre del Colegio"
                      name="school"
                      value={formData.school}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Orientación */}
                  <FormControl id="orientation">
                    <FormLabel>Orientación</FormLabel>
                    <Input
                      placeholder="Orientación del estudiante"
                      name="orientation"
                      value={formData.orientation}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Formulario 69A */}
                  <FormControl id="form_69A">
                    <FormLabel>Formulario 69A</FormLabel>
                    <Input
                      type="file"
                      name="form_69A"
                      onChange={handleFileChange} // Asegúrate de crear este manejador para los archivos
                    />
                  </FormControl>

                  {/* Expedida por */}
                  <FormControl id="issued_by">
                    <FormLabel>Expedida por</FormLabel>
                    <Input
                      placeholder="Entidad o persona que expidió el Formulario 69A"
                      name="issued_by"
                      value={formData.issued_by}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Fecha del último examen */}
                  <FormControl id="last_exam_date">
                    <FormLabel>Fecha del último examen</FormLabel>
                    <Input
                      type="date"
                      name="last_exam_date"
                      value={formData.last_exam_date}
                      onChange={handleInputChange}
                    />
                  </FormControl>

                  {/* Departamento */}
                  <FormControl id="department">
                    <FormLabel>Departamento</FormLabel>
                    <Input
                      placeholder="Departamento del estudiante"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </SimpleGrid>
              )}
              {activeStep === 4 && (
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mt={4}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    mb={6}
                    gridColumn="span 2"
                    color="gray.700"
                  >
                    Confirmación de Datos
                  </Text>

                  {/* Datos Personales */}
                  <GridItem colSpan={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={3}
                    >
                      Datos Personales
                    </Text>
                    <Text>
                      <strong>Nombre Completo:</strong> {formData.name}{' '}
                      {formData.secondName} {formData.lastName}{' '}
                      {formData.maternalLastName}
                    </Text>
                    <Text>
                      <strong>Tipo de Documento:</strong>{' '}
                      {(() => {
                        const selected = documentTypes?.find(
                          (dt) => String(dt.id) === String(formData.documentTypeId),
                        );
                        return selected?.description || '—';
                      })()}
                    </Text>
                    <Text>
                      <strong>Documento:</strong> {formData.document}
                    </Text>
                    <Text>
                      <strong>Fecha de Nacimiento:</strong> {formData.birthDate}
                    </Text>
                    <Text>
                      <strong>Sexo:</strong>{' '}
                      {(() => {
                        const selected = sexTypes?.find(
                          (st) => String(st.id) === String(formData.sexId),
                        );
                        return selected?.description || '—';
                      })()}
                    </Text>
                  </GridItem>

                  {/* Espacio entre secciones */}
                  <Box
                    gridColumn="span 2"
                    height="1px"
                    bg="transparent"
                    my={4}
                  />

                  {/* Contacto */}
                  <GridItem colSpan={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={3}
                    >
                      Contacto
                    </Text>
                    <Text>
                      <strong>Teléfono:</strong> {formData.phone}
                    </Text>
                    <Text>
                      <strong>Celular:</strong> {formData.cellPhone}
                    </Text>
                    <Text>
                      <strong>Correo Electrónico:</strong> {formData.email}
                    </Text>
                  </GridItem>

                  {/* Espacio entre secciones */}
                  <Box
                    gridColumn="span 2"
                    height="1px"
                    bg="transparent"
                    my={4}
                  />

                  {/* Dirección */}
                  <GridItem colSpan={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={3}
                    >
                      Dirección
                    </Text>
                    <Text>
                      <strong>Calle:</strong> {formData.street}
                    </Text>
                    <Text>
                      <strong>Número:</strong> {formData.number}
                    </Text>
                    <Text>
                      <strong>Apartamento:</strong> {formData.apartment}
                    </Text>
                    <Text>
                      <strong>Ciudad:</strong> {formData.city}
                    </Text>
                    <Text>
                      <strong>Localidad:</strong> {formData.locality}
                    </Text>
                    <Text>
                      <strong>País:</strong> {formData.country}
                    </Text>
                  </GridItem>

                  {/* Espacio entre secciones */}
                  <Box
                    gridColumn="span 2"
                    height="1px"
                    bg="transparent"
                    my={4}
                  />

                  {/* Contacto de Emergencia */}
                  <GridItem colSpan={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={3}
                    >
                      Contacto de Emergencia
                    </Text>
                    <Text>
                      <strong>Contacto:</strong> {formData.emergencyContact}
                    </Text>
                    <Text>
                      <strong>Teléfono:</strong>{' '}
                      {formData.emergencyContactPhone}
                    </Text>
                    <Text>
                      <strong>Sociedad médica:</strong>{' '}
                      {formData.medicalInsurance}
                    </Text>
                    <Text>
                      <strong>Emergencia móvil:</strong>{' '}
                      {formData.mobileEmergency}
                    </Text>
                  </GridItem>

                  {/* Espacio entre secciones */}
                  <Box
                    gridColumn="span 2"
                    height="1px"
                    bg="transparent"
                    my={4}
                  />

                  {/* Información Académica */}
                  <GridItem colSpan={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={3}
                    >
                      Información Académica
                    </Text>
                    <Text>
                      <strong>Colegio:</strong> {formData.school}
                    </Text>
                    <Text>
                      <strong>Orientación:</strong> {formData.orientation}
                    </Text>
                    <Text>
                      <strong>Formulario 69A:</strong>{' '}
                      {formData.form_69A ? 'Archivo cargado' : 'No cargado'}
                    </Text>
                    <Text>
                      <strong>Expedido por:</strong> {formData.issued_by}
                    </Text>
                    <Text>
                      <strong>Fecha del Último Examen:</strong>{' '}
                      {formData.last_exam_date}
                    </Text>
                    <Text>
                      <strong>Departamento:</strong> {formData.department}
                    </Text>
                  </GridItem>
                </SimpleGrid>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Box>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handlePrevStep}
              isDisabled={activeStep === 0}
              width="120px" // Fija el tamaño del botón
            >
              Atrás
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                colorScheme="blue"
                onClick={handleSave}
                width="120px" // Fija el tamaño del botón
              >
                Completar
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                onClick={handleNextStep}
                isDisabled={activeStep === steps.length - 1}
                width="120px" // Fija el tamaño del botón
              >
                Siguiente
              </Button>
            )}
          </Box>
        </ModalFooter>
        <Box display={{ base: 'block', xl: 'none' }} mt={4}>
          <Stack spacing={4}>
            <Stepper size="sm" index={activeStep} gap="0">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepIndicator>
                    <StepStatus complete={<StepIcon />} />
                  </StepIndicator>
                  <StepSeparator _horizontal={{ ml: '0' }} />
                </Step>
              ))}
            </Stepper>
          </Stack>
        </Box>
      </ModalContent>
    </Modal>
  );
}
